'use client';

import { useState, useRef, useEffect } from 'react';
import type Cropper from 'cropperjs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog/dialog';
import { Button } from '@/components/ui/button/button';
import { Loader2 } from 'lucide-react';

interface ImageCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  aspect?: number;
  title?: string;
  onCropped: (file: File) => void | Promise<void>;
  outputFilename?: string;
  outputType?: string;
  outputQuality?: number;
  /** Output size in pixels (square). Defaults to 512. */
  outputSize?: number;
  /** Background color for padding areas. Defaults to white. */
  backgroundColor?: string;
}

function getCropperTemplate(aspect?: number): string {
  const aspectRatioAttribute = aspect ? ` aspect-ratio="${aspect}"` : '';

  return (
    '<cropper-canvas background style="width:100%;height:100%;">' +
    '<cropper-image initial-center-size="contain" scalable translatable></cropper-image>' +
    '<cropper-shade hidden></cropper-shade>' +
    '<cropper-handle action="select" plain></cropper-handle>' +
    `<cropper-selection${aspectRatioAttribute} initial-coverage="0.8" movable resizable>` +
    '<cropper-grid role="grid" covered></cropper-grid>' +
    '<cropper-crosshair centered></cropper-crosshair>' +
    '<cropper-handle action="move" theme-color="rgba(255, 255, 255, 0.35)"></cropper-handle>' +
    '<cropper-handle action="n-resize"></cropper-handle>' +
    '<cropper-handle action="e-resize"></cropper-handle>' +
    '<cropper-handle action="s-resize"></cropper-handle>' +
    '<cropper-handle action="w-resize"></cropper-handle>' +
    '<cropper-handle action="ne-resize"></cropper-handle>' +
    '<cropper-handle action="nw-resize"></cropper-handle>' +
    '<cropper-handle action="se-resize"></cropper-handle>' +
    '<cropper-handle action="sw-resize"></cropper-handle>' +
    '</cropper-selection>' +
    '</cropper-canvas>'
  );
}

export function ImageCropDialog({
  open,
  onOpenChange,
  imageSrc,
  aspect,
  title = 'Crop Image',
  onCropped,
  outputFilename = 'cropped.png',
  outputType = 'image/png',
  outputQuality = 0.92,
  outputSize = 1024,
  backgroundColor = '#ffffff',
}: ImageCropDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const cropperRef = useRef<Cropper | null>(null);

  useEffect(() => {
    if (!open) {
      setImageLoaded(false);
      setReady(false);
      return;
    }

    setReady(false);
  }, [open, imageSrc]);

  useEffect(() => {
    if (!open || !imageLoaded) return;

    const container = containerRef.current;
    const image = imageRef.current;

    if (!container || !image) return;

    let cancelled = false;

    void import('cropperjs/dist/cropper.esm.js')
      .then(({ default: Cropper }) => {
        if (cancelled || !container.isConnected || !image.isConnected) return;

        const cropper = new Cropper(image, {
          container,
          template: getCropperTemplate(aspect),
        });

        cropperRef.current = cropper;
        setReady(Boolean(cropper.getCropperSelection()));
      })
      .catch((error) => {
        console.error('Failed to initialize Cropper.js', error);
      });

    return () => {
      cancelled = true;
      if (cropperRef.current) {
        cropperRef.current.destroy();
        cropperRef.current = null;
      }
      setReady(false);
    };
  }, [open, imageLoaded, aspect]);

  const handleConfirm = async () => {
    const cropper = cropperRef.current;
    if (!cropper) return;

    const selection = cropper.getCropperSelection();
    if (!selection) return;

    setIsSaving(true);
    try {
      const croppedCanvas = await selection.$toCanvas();

      // Fit the cropped region into a square canvas with background padding
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = outputSize;
      outputCanvas.height = outputSize;
      const ctx = outputCanvas.getContext('2d')!;

      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, outputSize, outputSize);

      const cropAspect = croppedCanvas.width / croppedCanvas.height;
      let drawW: number;
      let drawH: number;

      if (cropAspect > 1) {
        drawW = outputSize;
        drawH = outputSize / cropAspect;
      } else {
        drawH = outputSize;
        drawW = outputSize * cropAspect;
      }

      const drawX = (outputSize - drawW) / 2;
      const drawY = (outputSize - drawH) / 2;

      ctx.drawImage(croppedCanvas, drawX, drawY, drawW, drawH);

      const blob = await new Promise<Blob>((resolve, reject) => {
        outputCanvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
          outputType,
          outputQuality,
        );
      });

      const file = new File([blob], outputFilename, { type: outputType });
      await onCropped(file);
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Select the region to use. The result will be fitted into a square with padding.
        </p>

        <div
          ref={containerRef}
          className="relative w-full overflow-hidden rounded-md border bg-black/5"
          style={{ minHeight: 300, height: '60vh', maxHeight: '60vh' }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            key={imageSrc}
            ref={imageRef}
            src={imageSrc}
            alt="Crop source"
            crossOrigin={imageSrc.startsWith('blob:') ? undefined : 'anonymous'}
            className="block max-h-full max-w-full object-contain"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(false)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void handleConfirm()} disabled={isSaving || !ready}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cropping...
              </>
            ) : (
              'Confirm Crop'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
